import { Box, Stack, Typography, useTheme } from '@mui/material'
import { get } from 'lodash'
import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { isLibbyDomain } from '~/features/partners/utils'
import { EClientPath } from '~/models/api'
import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

function NotFoundPage() {
  const theme = useTheme()
  const { state } = useLocation()
  const isReplifyDomain = isLibbyDomain()

  const onLoadRef = useRef(() => {
    const fromPathname = get(state, 'from', '/404')

    window.history.replaceState(null, '', fromPathname)
  })

  const background = isReplifyDomain
    ? theme.palette.menuBackground
    : theme.palette.tertiary2

  const textColor = getWhiteOrBlackTextColorByBackground(background, theme)

  // Run only once at mount
  useEffect(() => onLoadRef.current(), [])

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        position: 'relative',
        backgroundColor: background,
        width: '100vw',
        height: '100dvh',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={4}
        sx={{ zIndex: 2 }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: rem(14),
            textTransform: 'uppercase',
            fontWeight: 500,
            color: textColor,
            letterSpacing: rem(2),
          }}
        >
          Page not found
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontSize: rem(144),
            fontWeight: 500,
            color: theme.palette.primaryButtonBg,
          }}
        >
          404
        </Typography>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xxxs: rem(32), md: rem(52) },
            fontWeight: 500,
            color: textColor,
            textAlign: 'center',
          }}
        >
          Sorry, we were unable to find that page
        </Typography>
        <Box
          component={Link}
          to={EClientPath.home}
          sx={{
            backgroundColor: theme.palette.primaryButtonBg,
            color: theme.palette.primaryButtonFont,
            textDecoration: 'none',
            paddingBlock: rem(12),
            paddingInline: rem(48),
            borderRadius: rem(100),
            fontWeight: 500,
          }}
        >
          Go home
        </Box>
      </Stack>

      <Box
        component="svg"
        viewBox="0 0 1440 170"
        sx={{ position: 'absolute', bottom: 0, zIndex: 1 }}
      >
        <defs>
          <linearGradient id="sw-gradient-0" x1="0" x2="0" y1="1" y2="0">
            <stop stopColor={theme.palette.primaryButtonBg} offset="0%" />
            <stop stopColor={background} offset="100%" />
          </linearGradient>
        </defs>
        <path
          fill="url(#sw-gradient-0)"
          d="M0,68L80,73.7C160,79,320,91,480,93.5C640,96,800,91,960,76.5C1120,62,1280,40,1440,45.3C1600,51,1760,85,1920,104.8C2080,125,2240,130,2400,119C2560,108,2720,79,2880,68C3040,57,3200,62,3360,79.3C3520,96,3680,125,3840,116.2C4000,108,4160,62,4320,56.7C4480,51,4640,85,4800,93.5C4960,102,5120,85,5280,76.5C5440,68,5600,68,5760,73.7C5920,79,6080,91,6240,99.2C6400,108,6560,113,6720,121.8C6880,130,7040,142,7200,127.5C7360,113,7520,74,7680,51C7840,28,8000,23,8160,19.8C8320,17,8480,17,8640,14.2C8800,11,8960,6,9120,22.7C9280,40,9440,79,9600,99.2C9760,119,9920,119,10080,119C10240,119,10400,119,10560,119C10720,119,10880,119,11040,102C11200,85,11360,51,11440,34L11520,17L11520,170L11440,170C11360,170,11200,170,11040,170C10880,170,10720,170,10560,170C10400,170,10240,170,10080,170C9920,170,9760,170,9600,170C9440,170,9280,170,9120,170C8960,170,8800,170,8640,170C8480,170,8320,170,8160,170C8000,170,7840,170,7680,170C7520,170,7360,170,7200,170C7040,170,6880,170,6720,170C6560,170,6400,170,6240,170C6080,170,5920,170,5760,170C5600,170,5440,170,5280,170C5120,170,4960,170,4800,170C4640,170,4480,170,4320,170C4160,170,4000,170,3840,170C3680,170,3520,170,3360,170C3200,170,3040,170,2880,170C2720,170,2560,170,2400,170C2240,170,2080,170,1920,170C1760,170,1600,170,1440,170C1280,170,1120,170,960,170C800,170,640,170,480,170C320,170,160,170,80,170L0,170Z"
        />
      </Box>
    </Stack>
  )
}

export default NotFoundPage
